import { useState, useEffect, useCallback } from "react";
import { IReportData } from "pages/reports-menu/types";
import { getUpcomingMilestonesReport } from "services/report-services";
import { HTTPClientResponse } from "services/ciq-client";

function formatReportLogItems(reportItems: IReportData["items"]) {
  const formattedItems: any[] = [];

  if (reportItems.length > 0) {
    reportItems.forEach((item) => {
      if (item.submittals.length > 0) {
        item.submittals.forEach((submittal) => {
          formattedItems.push({
            gc_user_full_name: `${item.gc_user.first_name} ${item.gc_user.last_name}`,
            type: "Submittals",
            title: submittal.title,
            milestone_state: submittal.milestone_state,
            next_planned_deadline: submittal.next_planned_deadline,
            risk_level: submittal.risk_level,
            assignee_user: submittal.assignee_user,
            assignee_vendor: submittal.assignee_vendor,
            id: submittal.id
          });
        });
      }
      if (item.materials.length > 0) {
        item.materials.forEach((material) => {
          formattedItems.push({
            gc_user_full_name: `${item.gc_user.first_name} ${item.gc_user.last_name}`,
            type: "Materials",
            title: material.title,
            milestone_state: material.milestone_state,
            next_planned_deadline: material.next_planned_deadline,
            risk_level: material.risk_level,
            assignee_user: material.assignee_user,
            assignee_vendor: material.assignee_vendor,
            id: material.id
          });
        });
      }
    });
  }

  return formattedItems;
}

function useUpcomingMilestonesReport(projectId: string, projectToken: string) {
  const [reportData, setReportData] = useState<
    (IReportData & { formattedItems: any[] }) | null
  >(null);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchReportData = useCallback(async () => {
    if (!projectId || !projectToken) return;

    setIsLoading(true);
    setError(null);

    const response: HTTPClientResponse = await getUpcomingMilestonesReport(
      projectId,
      projectToken
    );

    if (response.success) {
      const formattedItems = formatReportLogItems(
        response.data?.data.items || []
      );
      setReportData({
        ...response.data?.data,
        formattedItems
      });
    } else {
      setError(
        new Error(response.errorMsg || "Failed to fetch milestone report")
      );
    }

    setIsLoading(false);
  }, [projectId, projectToken]);

  useEffect(() => {
    fetchReportData();
  }, [fetchReportData, projectId, projectToken]);

  return { reportData, fetchReportData, isLoading, error };
}

export default useUpcomingMilestonesReport;
